import React, { Component } from 'react'
import { toast } from 'react-toastify'
import { getString, getLocale } from '../../../locale'
import {
    getSystemInfo,
    getSystemStatus,
    getBrightness,
    setBrightness,
    setTime,
    syncTime,
    getTimezoneList,
    getTimezone,
    setTimezone,
    getLanguage,
    setLanguage,
    restartSystem,
    getUserSecurity,
    lockSystemNow,
    adminAuthKey,
    adminRelock,
    exportSettings,
    importSettings,
    listSettingsProfiles,
    getSettingsProfile,
    saveSettingsProfileFromMachine,
    SystemInfo,
    SystemStatus,
    TimezoneInfo,
} from '../../../api/0x0005/system'
import { askYesNo } from '../../../window/ask-yes-no'
import { Window } from '../../../window'

type Props = {
    clientId: string
}

type State = {
    systemInfo?: SystemInfo
    systemStatus?: SystemStatus
    brightness: number
    timezones: TimezoneInfo[]
    currentTimezoneIndex: number
    currentLanguage: string
    loading: boolean
    showTimeModal: boolean
    showTimezoneModal: boolean
    showLanguageModal: boolean
    lockOnStartup: boolean
    form: {
        year: number
        month: number
        day: number
        hour: number
        minute: number
    }
}

export default class SystemTab extends Component<Props, State> {
    private statusTimer?: NodeJS.Timeout

    state: State = {
        systemInfo: undefined,
        systemStatus: undefined,
        brightness: 50,
        timezones: [],
        currentTimezoneIndex: 0,
        currentLanguage: 'en',
        loading: true,
        showTimeModal: false,
        showTimezoneModal: false,
        showLanguageModal: false,
        lockOnStartup: false,
        form: {
            year: new Date().getFullYear(),
            month: new Date().getMonth() + 1,
            day: new Date().getDate(),
            hour: new Date().getHours(),
            minute: new Date().getMinutes(),
        },
    }

    async componentDidMount() {
        await this.loadSystemInfo()
        await this.loadBrightness()
        await this.loadTimezones()
        await this.loadCurrentTimezone()
        await this.loadLanguage()
        await this.loadUserSecurity()
        await this.loadSystemStatus()

        // Start auto-refresh timer for system status (every 2 seconds)
        this.statusTimer = setInterval(() => {
            this.loadSystemStatus()
        }, 2000)
    }

    componentWillUnmount() {
        // Clear the status refresh timer
        if (this.statusTimer) {
            clearInterval(this.statusTimer)
        }
    }

    loadSystemInfo = async () => {
        try {
            const response = await getSystemInfo(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({
                    systemInfo: {
                        appVersion: response.data.appVersion,
                        idfVersion: response.data.idfVersion,
                        partName: response.data.partName,
                        compileDate: response.data.compileDate,
                        compileTime: response.data.compileTime,
                        cores: response.data.cores,
                        revision: response.data.revision,
                        flashSize: response.data.flashSize,
                        wifiMac: response.data.wifiMac,
                        btMac: response.data.btMac,
                    }
                })
            }
        } catch (error: any) {
            console.error('Failed to load system info:', error)
        }
    }

    loadSystemStatus = async () => {
        try {
            const response = await getSystemStatus(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({
                    systemStatus: {
                        uptimeMs: response.data.uptimeMs,
                        freeHeap: response.data.freeHeap,
                        rtcTime: response.data.rtcTime,
                        rtcDate: response.data.rtcDate,
                        timezone: response.data.timezone,
                    }
                })
            }
        } catch (error: any) {
            console.error('Failed to load system status:', error)
        }
    }

    loadBrightness = async () => {
        try {
            const response = await getBrightness(this.props.clientId)
            if (response.data.success === 1) {
                // Snap loaded brightness to nearest 10% increment
                const displayPercent = Math.round(((response.data.brightness - 10) / 85) * 100)
                const snappedDisplayPercent = Math.round(displayPercent / 10) * 10
                const snappedBrightness = Math.round(10 + (snappedDisplayPercent / 100) * 85)
                this.setState({ brightness: snappedBrightness })
            }
        } catch (error: any) {
            console.error('Failed to load brightness:', error)
        } finally {
            this.setState({ loading: false })
        }
    }

    loadTimezones = async () => {
        try {
            const response = await getTimezoneList(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ timezones: response.data.timezones })
            }
        } catch (error: any) {
            console.error('Failed to load timezones:', error)
        }
    }

    loadCurrentTimezone = async () => {
        try {
            const response = await getTimezone(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ currentTimezoneIndex: response.data.timezoneIndex })
            }
        } catch (error: any) {
            console.error('Failed to load current timezone:', error)
        }
    }

    loadLanguage = async () => {
        try {
            const response = await getLanguage(this.props.clientId)
            if (response.data.success === 1) {
                this.setState({ currentLanguage: response.data.language })
            }
        } catch (error: any) {
            console.error('Failed to load language:', error)
        }
    }

    loadUserSecurity = async () => {
        try {
            const response = await getUserSecurity(this.props.clientId)
            this.setState({ lockOnStartup: response?.data?.lockOnStartup === 1 })
        } catch (error: any) {
            console.error('Failed to load user security:', error)
        }
    }

    handleBrightnessChange = async (displayDelta: number) => {
        // Calculate current display percentage (0-100)
        const currentDisplayPercent = Math.round(((this.state.brightness - 10) / 85) * 100)

        // Calculate new display percentage and snap to 10% increments
        let newDisplayPercent = currentDisplayPercent + displayDelta
        newDisplayPercent = Math.max(0, Math.min(100, newDisplayPercent))
        newDisplayPercent = Math.round(newDisplayPercent / 10) * 10 // Snap to nearest 10%

        // Convert back to actual brightness (10-95 range)
        const newBrightness = Math.round(10 + (newDisplayPercent / 100) * 85)

        try {
            const response = await setBrightness(this.props.clientId, newBrightness)
            if (response.data.success === 1) {
                this.setState({ brightness: newBrightness })
            } else {
                toast.error(response.data.errorMsg || getString('system.brightness_error'))
            }
        } catch (error: any) {
            console.error('Failed to set brightness:', error)
            toast.error(getString('system.brightness_error'))
        }
    }

    handleSetTime = async () => {
        const { year, month, day, hour, minute } = this.state.form

        try {
            const response = await setTime(this.props.clientId, { year, month, day, hour, minute })
            if (response.data.success === 1) {
                toast.success(getString('system.time_updated'))
                this.setState({ showTimeModal: false })
                await this.loadSystemStatus()
            } else {
                toast.error(response.data.errorMsg || getString('system.time_error'))
            }
        } catch (error: any) {
            console.error('Failed to set time:', error)
            toast.error(getString('system.time_error'))
        }
    }

    handleSyncTime = async () => {
        try {
            toast.info(getString('system.time_syncing'))
            const response = await syncTime(this.props.clientId)
            if (response.data.success === 1) {
                toast.success(getString('system.time_synced'))
                await this.loadSystemStatus()
            } else {
                toast.error(response.data.errorMsg || getString('system.time_sync_error'))
            }
        } catch (error: any) {
            console.error('Failed to sync time:', error)
            toast.error(getString('system.time_sync_error'))
        }
    }

    handleTimezoneChange = async (timezone: TimezoneInfo) => {
        try {
            const response = await setTimezone(this.props.clientId, timezone.posixTz)
            if (response.data.success === 1) {
                this.setState({ currentTimezoneIndex: timezone.index, showTimezoneModal: false })
                toast.success(getString('system.timezone_updated'))
                await this.loadSystemStatus()
            } else {
                toast.error(response.data.errorMsg || getString('system.timezone_error'))
            }
        } catch (error: any) {
            console.error('Failed to set timezone:', error)
            toast.error(getString('system.timezone_error'))
        }
    }

    handleLanguageChange = async (language: string) => {
        try {
            const response = await setLanguage(this.props.clientId, language)
            if (response.data.success === 1) {
                this.setState({ currentLanguage: language, showLanguageModal: false })
                toast.success(getString('system.language_updated'))
            } else {
                toast.error(response.data.errorMsg || getString('system.language_error'))
            }
        } catch (error: any) {
            console.error('Failed to set language:', error)
            toast.error(getString('system.language_error'))
        }
    }

    handleRestart = async () => {
        const confirmed = await askYesNo(getString('system.restart_confirm'))
        if (!confirmed) return

        try {
            const response = await restartSystem(this.props.clientId)
            if (response.data.success === 1) {
                toast.success(getString('system.restart_success'))
            } else {
                toast.error(response.data.errorMsg || getString('system.restart_error'))
            }
        } catch (error: any) {
            console.error('Failed to restart system:', error)
            toast.error(getString('system.restart_error'))
        }
    }

    handleLockNow = async () => {
        try {
            const response = await lockSystemNow(this.props.clientId)
            if (response.data.success === 1) {
                toast.success(getString('system.lock_now_success'))
            } else {
                toast.error(response.data.errorMsg || getString('system.lock_now_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('system.lock_now_error'))
        }
    }

    handleAdminAuthKey = async () => {
        try {
            const response = await adminAuthKey(this.props.clientId)
            if (response.data.success === 1) {
                toast.success(getString('system.admin_auth_success'))
            } else {
                toast.error(response.data.errorMsg || getString('system.admin_auth_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('system.admin_auth_error'))
        }
    }

    handleAdminRelock = async () => {
        try {
            const response = await adminRelock(this.props.clientId)
            if (response.data.success === 1) {
                toast.success(getString('system.admin_relock_success'))
            } else {
                toast.error(response.data.errorMsg || getString('system.admin_relock_error'))
            }
        } catch (error: any) {
            toast.error(error?.message || getString('system.admin_relock_error'))
        }
    }

    handleExportSettings = async () => {
        try {
            const askName = await new Window().fire({
                title: getString('system.export_name_prompt'),
                input: 'text',
                inputLabel: getString('system.export_name_label'),
                showCancelButton: true,
                inputValidator: value => {
                    const v = String(value || '').trim()
                    if (!v) return getString('system.export_name_required')
                    return undefined
                },
            })
            if (!askName.isConfirmed) return
            const profileName = String(askName.value || '').trim()
            const response = await saveSettingsProfileFromMachine(this.props.clientId, profileName)

            // Keep existing behavior: also download the exported json locally.
            const blob = new Blob([response.data.jsonData], { type: 'application/json' })
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.href = url
            a.download = `${profileName}.json`
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
            document.body.removeChild(a)

            toast.success(getString('system.export_saved_success', profileName))
        } catch (error: any) {
            console.error('Failed to export settings:', error)
            toast.error(error?.message || getString('system.export_error'))
        }
    }

    handleImportSettings = async () => {
        // Create a hidden file input
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'application/json,.json'

        input.onchange = async (e: any) => {
            const file = e.target.files?.[0]
            if (!file) return

            const confirmed = await askYesNo(getString('system.import_confirm'))
            if (!confirmed) return

            try {
                const reader = new FileReader()
                reader.onload = async (event) => {
                    try {
                        const jsonData = event.target?.result as string
                        const response = await importSettings(this.props.clientId, jsonData)

                        if (response.data.success === 1) {
                            toast.success(getString('system.import_success'))
                            // Reload system info and settings after import
                            await this.loadSystemInfo()
                            await this.loadBrightness()
                            await this.loadCurrentTimezone()
                            await this.loadLanguage()
                        } else {
                            toast.error(response.data.errorMsg || getString('system.import_error'))
                        }
                    } catch (error: any) {
                        console.error('Failed to import settings:', error)
                        toast.error(getString('system.import_error'))
                    }
                }
                reader.readAsText(file)
            } catch (error: any) {
                console.error('Failed to read file:', error)
                toast.error(getString('system.import_error'))
            }
        }

        // Trigger file picker
        input.click()
    }

    handleImportSavedSettings = async () => {
        try {
            const profiles = (await listSettingsProfiles()).data
            if (!profiles || profiles.length === 0) {
                toast.error(getString('system.saved_profiles_empty'))
                return
            }
            const inputOptions: Record<string, string> = {}
            for (const profile of profiles) {
                inputOptions[profile.name] = profile.name
            }
            const askProfile = await new Window().fire({
                title: getString('system.import_saved_settings'),
                input: 'select',
                inputOptions,
                showCancelButton: true,
            })
            if (!askProfile.isConfirmed) return

            const profileName = String(askProfile.value || '')
            const confirmed = await askYesNo(getString('system.import_confirm'))
            if (!confirmed) return

            const profile = (await getSettingsProfile(profileName)).data
            const response = await importSettings(this.props.clientId, profile.jsonData)
            if (response.data.success === 1) {
                toast.success(getString('system.import_success'))
                await this.loadSystemInfo()
                await this.loadBrightness()
                await this.loadCurrentTimezone()
                await this.loadLanguage()
            } else {
                toast.error(response.data.errorMsg || getString('system.import_error'))
            }
        } catch (error: any) {
            console.error('Failed to import saved settings:', error)
            toast.error(error?.message || getString('system.import_error'))
        }
    }

    formatUptime = (uptimeMs: number): string => {
        const seconds = Math.floor(uptimeMs / 1000)
        const minutes = Math.floor(seconds / 60)
        const hours = Math.floor(minutes / 60)
        const days = Math.floor(hours / 24)

        if (days > 0) {
            return `${days}d ${hours % 24}h ${minutes % 60}m`
        } else if (hours > 0) {
            return `${hours}h ${minutes % 60}m ${seconds % 60}s`
        } else if (minutes > 0) {
            return `${minutes}m ${seconds % 60}s`
        } else {
            return `${seconds}s`
        }
    }

    formatMemory = (bytes: number): string => {
        const kb = bytes / 1024
        const mb = kb / 1024
        return mb >= 1 ? `${mb.toFixed(2)} MB` : `${kb.toFixed(2)} KB`
    }

    render() {
        const { systemInfo, systemStatus, brightness, timezones, currentTimezoneIndex, currentLanguage, loading, showTimeModal, showTimezoneModal, showLanguageModal, lockOnStartup, form } = this.state
        const locale = getLocale()

        if (loading) {
            return (
                <div className="flex items-center justify-center h-64">
                    <div className="text-lg text-gray-600 dark:text-gray-400">{getString('common.loading')}</div>
                </div>
            )
        }

        return (
            <div className="p-6 space-y-6 bg-white dark:bg-gray-900">
                {/* System Information Section */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('system.info_section')}</h2>

                    {systemInfo && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.app_version')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.appVersion}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.idf_version')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.idfVersion}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.part_name')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.partName}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.compile_date')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.compileDate}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.compile_time')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.compileTime}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.cores')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.cores}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.revision')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.revision}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.flash_size')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{this.formatMemory(systemInfo.flashSize)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.wifi_mac')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.wifiMac}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.bt_mac')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemInfo.btMac}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* System Status Section */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('system.status_section')}</h2>

                    {systemStatus && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.uptime')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{this.formatUptime(systemStatus.uptimeMs)}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.rtc_time')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemStatus.rtcTime}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.rtc_date')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemStatus.rtcDate}</span>
                            </div>
                            <div className="flex justify-between items-center">
                                <span className="font-semibold text-gray-700 dark:text-gray-300">{getString('system.timezone')}:</span>
                                <span className="text-gray-600 dark:text-gray-400">{systemStatus.timezone}</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Settings Section */}
                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-6">
                    <h2 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('system.settings_section')}</h2>

                    <div className="space-y-4">
                        {/* Brightness Control */}
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                            <div className="flex justify-between items-center mb-3">
                                <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {getString('system.brightness')}
                                </label>
                                <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                                    {Math.round(Math.round(((brightness - 10) / 85) * 100) / 10) * 10}%
                                </span>
                            </div>
                            <div className="flex items-center gap-4">
                                <button
                                    onClick={() => this.handleBrightnessChange(-10)}
                                    disabled={brightness <= 10}
                                    className="w-10 h-10 flex items-center justify-center bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-xl font-bold"
                                >
                                    −
                                </button>
                                <style dangerouslySetInnerHTML={{
                                    __html: `
                                    input[type="range"]::-webkit-slider-thumb {
                                        -webkit-appearance: none !important;
                                        appearance: none !important;
                                        width: 32px !important;
                                        height: 32px !important;
                                        border-radius: 50% !important;
                                        background: white !important;
                                        cursor: pointer !important;
                                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
                                        border: 2px solid #3b82f6 !important;
                                    }
                                    input[type="range"]::-moz-range-thumb {
                                        width: 32px !important;
                                        height: 32px !important;
                                        border-radius: 50% !important;
                                        background: white !important;
                                        cursor: pointer !important;
                                        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
                                        border: 2px solid #3b82f6 !important;
                                    }
                                `}} />
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="10"
                                    value={Math.round(Math.round(((brightness - 10) / 85) * 100) / 10) * 10}
                                    onChange={(e) => {
                                        const displayPercent = parseInt(e.target.value)
                                        const newBrightness = Math.round(10 + (displayPercent / 100) * 85)
                                        this.setState({ brightness: newBrightness })
                                    }}
                                    onMouseUp={(e) => {
                                        const displayPercent = parseInt((e.target as HTMLInputElement).value)
                                        const newBrightness = Math.round(10 + (displayPercent / 100) * 85)
                                        this.handleBrightnessChange(displayPercent - Math.round(((brightness - 10) / 85) * 100))
                                    }}
                                    onTouchEnd={(e) => {
                                        const displayPercent = parseInt((e.target as HTMLInputElement).value)
                                        const newBrightness = Math.round(10 + (displayPercent / 100) * 85)
                                        this.handleBrightnessChange(displayPercent - Math.round(((brightness - 10) / 85) * 100))
                                    }}
                                    className="flex-1 h-3 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-8 [&::-webkit-slider-thumb]:h-8 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-xl [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-blue-500 [&::-moz-range-thumb]:w-8 [&::-moz-range-thumb]:h-8 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-blue-500 [&::-moz-range-thumb]:shadow-xl"
                                    style={{
                                        background: locale === 'he'
                                            ? `linear-gradient(to left, #3b82f6 0%, #3b82f6 ${Math.round(Math.round(((brightness - 10) / 85) * 100) / 10) * 10}%, #e5e7eb ${Math.round(Math.round(((brightness - 10) / 85) * 100) / 10) * 10}%, #e5e7eb 100%)`
                                            : `linear-gradient(to right, #3b82f6 0%, #3b82f6 ${Math.round(Math.round(((brightness - 10) / 85) * 100) / 10) * 10}%, #e5e7eb ${Math.round(Math.round(((brightness - 10) / 85) * 100) / 10) * 10}%, #e5e7eb 100%)`
                                    }}
                                />
                                <button
                                    onClick={() => this.handleBrightnessChange(10)}
                                    disabled={brightness >= 95}
                                    className="w-10 h-10 flex items-center justify-center bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-xl font-bold"
                                >
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Language Selection */}
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                {getString('system.language')}
                            </label>
                            <button
                                onClick={() => this.setState({ showLanguageModal: true })}
                                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-left font-medium transition-colors"
                            >
                                {currentLanguage === 'en' ? 'English' : 'עברית'}
                            </button>
                        </div>

                        {/* Time & Date */}
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                {getString('system.time_date')}
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => {
                                        const now = new Date()
                                        this.setState({
                                            showTimeModal: true,
                                            form: {
                                                year: now.getFullYear(),
                                                month: now.getMonth() + 1,
                                                day: now.getDate(),
                                                hour: now.getHours(),
                                                minute: now.getMinutes(),
                                            }
                                        })
                                    }}
                                    className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
                                >
                                    {getString('system.set_time')}
                                </button>
                                <button
                                    onClick={this.handleSyncTime}
                                    className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors"
                                >
                                    {getString('system.sync_time')}
                                </button>
                            </div>
                        </div>

                        {/* Timezone Selection */}
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                {getString('system.timezone')}
                            </label>
                            <button
                                onClick={() => this.setState({ showTimezoneModal: true })}
                                className="w-full px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-left font-medium transition-colors"
                            >
                                {timezones[currentTimezoneIndex]?.name || getString('system.select_timezone')}
                            </button>
                        </div>

                        {/* Settings Export/Import */}
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                            <label className="block text-sm font-semibold mb-3 text-gray-700 dark:text-gray-300">
                                {getString('system.export_settings')} / {getString('system.import_settings')}
                            </label>
                            <div className="flex gap-2">
                                <button
                                    onClick={this.handleExportSettings}
                                    className="flex-1 px-4 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium transition-colors"
                                >
                                    {getString('system.export_settings')}
                                </button>
                                <button
                                    onClick={this.handleImportSettings}
                                    className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium transition-colors"
                                >
                                    {getString('system.import_settings')}
                                </button>
                                <button
                                    onClick={this.handleImportSavedSettings}
                                    className="flex-1 px-4 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 font-medium transition-colors"
                                >
                                    {getString('system.import_saved_settings')}
                                </button>
                            </div>
                        </div>

                        {/* System Actions */}
                        <div className="bg-white dark:bg-gray-700 rounded-lg p-4 shadow-sm">
                            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                                <button
                                    onClick={this.handleRestart}
                                    className="w-full px-6 py-3 bg-red-500 text-white rounded-lg hover:bg-red-600 font-semibold transition-colors"
                                >
                                    {getString('system.restart')}
                                </button>
                                <button
                                    onClick={this.handleAdminAuthKey}
                                    className="w-full px-6 py-3 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 font-semibold transition-colors"
                                >
                                    {getString('system.admin_auth_send')}
                                </button>
                                <button
                                    onClick={this.handleAdminRelock}
                                    className="w-full px-6 py-3 rounded-lg font-semibold transition-colors"
                                    style={{ backgroundColor: '#f59e0b', color: '#ffffff' }}
                                >
                                    {getString('system.admin_relock_send')}
                                </button>
                                {lockOnStartup && (
                                    <button
                                        onClick={this.handleLockNow}
                                        className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 font-semibold transition-colors"
                                        style={{ backgroundColor: '#f97316', color: '#ffffff' }}
                                    >
                                        {getString('system.lock_now')}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Time/Date Modal */}
                {showTimeModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => this.setState({ showTimeModal: false })}>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('system.set_time')}</h3>

                            <div className="grid grid-cols-5 gap-2 mb-4">
                                <div>
                                    <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">{getString('system.year')}</label>
                                    <input
                                        type="number"
                                        value={form.year}
                                        onChange={(e) => this.setState({ form: { ...form, year: parseInt(e.target.value) || 2024 } })}
                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        min={2000}
                                        max={2099}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">{getString('system.month')}</label>
                                    <input
                                        type="number"
                                        value={form.month}
                                        onChange={(e) => this.setState({ form: { ...form, month: parseInt(e.target.value) || 1 } })}
                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        min={1}
                                        max={12}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">{getString('system.day')}</label>
                                    <input
                                        type="number"
                                        value={form.day}
                                        onChange={(e) => this.setState({ form: { ...form, day: parseInt(e.target.value) || 1 } })}
                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        min={1}
                                        max={31}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">{getString('system.hour')}</label>
                                    <input
                                        type="number"
                                        value={form.hour}
                                        onChange={(e) => this.setState({ form: { ...form, hour: parseInt(e.target.value) || 0 } })}
                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        min={0}
                                        max={23}
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs mb-1 text-gray-700 dark:text-gray-300">{getString('system.minute')}</label>
                                    <input
                                        type="number"
                                        value={form.minute}
                                        onChange={(e) => this.setState({ form: { ...form, minute: parseInt(e.target.value) || 0 } })}
                                        className="w-full px-2 py-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                        min={0}
                                        max={59}
                                    />
                                </div>
                            </div>

                            <div className="flex justify-between items-center">
                                <button
                                    onClick={() => {
                                        const now = new Date()
                                        this.setState({
                                            form: {
                                                year: now.getFullYear(),
                                                month: now.getMonth() + 1,
                                                day: now.getDate(),
                                                hour: now.getHours(),
                                                minute: now.getMinutes(),
                                            }
                                        })
                                    }}
                                    className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                                >
                                    {getString('system.use_pc_time')}
                                </button>
                                <div className="flex space-x-2">
                                    <button
                                        onClick={() => this.setState({ showTimeModal: false })}
                                        className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                                    >
                                        {getString('common.cancel')}
                                    </button>
                                    <button
                                        onClick={this.handleSetTime}
                                        className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                                    >
                                        {getString('common.save')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Timezone Modal */}
                {showTimezoneModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => this.setState({ showTimezoneModal: false })}>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full flex flex-col" style={{ maxHeight: '70vh' }} onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white flex-shrink-0">{getString('system.set_timezone')}</h3>

                            <div className="overflow-y-auto flex-1 mb-4 pr-2">
                                {timezones.map((tz) => (
                                    <button
                                        key={tz.index}
                                        onClick={() => this.handleTimezoneChange(tz)}
                                        className={`w-full text-left px-4 py-3 mb-2 rounded transition-colors ${currentTimezoneIndex === tz.index
                                                ? 'bg-blue-500 text-white'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                                            }`}
                                    >
                                        {tz.name} ({tz.offset})
                                    </button>
                                ))}
                            </div>

                            <div className="flex justify-end flex-shrink-0">
                                <button
                                    onClick={() => this.setState({ showTimezoneModal: false })}
                                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                                >
                                    {getString('common.cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Language Modal */}
                {showLanguageModal && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => this.setState({ showLanguageModal: false })}>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4" onClick={(e) => e.stopPropagation()}>
                            <h3 className="text-xl font-bold mb-4 text-gray-800 dark:text-white">{getString('system.language_select')}</h3>

                            <div className="space-y-2 mb-4">
                                <button
                                    onClick={() => this.handleLanguageChange('en')}
                                    className={`w-full text-left px-4 py-3 rounded transition-colors ${currentLanguage === 'en'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    English
                                </button>
                                <button
                                    onClick={() => this.handleLanguageChange('he')}
                                    className={`w-full text-left px-4 py-3 rounded transition-colors ${currentLanguage === 'he'
                                            ? 'bg-blue-500 text-white'
                                            : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-600'
                                        }`}
                                >
                                    עברית
                                </button>
                            </div>

                            <div className="flex justify-end">
                                <button
                                    onClick={() => this.setState({ showLanguageModal: false })}
                                    className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-white rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                                >
                                    {getString('common.cancel')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        )
    }
}
